import { K8sProviderIcon } from '@cast/features-cluster-ui';
import { useEffect, useMemo, useState } from 'react';

import * as yup from 'yup';

import 'core/extensions/yup';

import {
  Button,
  ConditionalWrapper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  notify,
  CloseDialogButton,
  Typography,
  Stack,
} from '@cast/design-system';
import { Cluster } from '@cast/types';
import { HibernationJobType } from '@cast/types-cluster-autoscaler';
import { makeValidateSync } from '@cast/utils';

import { getNodeJobConfigDefaultValue } from 'features/organization/cluster-hibernate/utils';
import { useCreateHibernationJobMutation } from 'hooks/mutations/cluster-autoscaler';
import { useGetHibernationSchedulesQuery } from 'hooks/queries/cluster-autoscaler';

import { DialogDescription } from './DialogDescription';
import { NodeConfiguration } from './NodeConfiguration';

import { RffForm } from '@cast/shared-rff-ui';

const getValidationSchema = () =>
  makeValidateSync(
    yup.object({
      nodeJobConfig: yup
        .string()
        .json()
        .validateJsonProp(
          'nodeConfig.instanceType',
          (value) => typeof value === 'string' && value !== ''
        )
        .required('Node configuration is required'),
    })
  );

type FormValues = {
  nodeJobConfig: string;
};

type ResumeClusterDialogProps = {
  cluster: Cluster;
  onClose: () => void;
};

export const ResumeClusterDialog = ({
  cluster,
  onClose,
}: ResumeClusterDialogProps) => {
  const { mutate: createHibernationJob, isPending: isResumePending } =
    useCreateHibernationJobMutation();
  const { hibernationSchedules, isLoading: isLoadingSchedules } =
    useGetHibernationSchedulesQuery();

  const assignedSchedulesToCluster = useMemo(() => {
    if (!hibernationSchedules?.length) {
      return [];
    }

    return hibernationSchedules.filter((schedule) =>
      schedule.clusterAssignments.items.some((c) => c.clusterId === cluster.id)
    );
  }, [hibernationSchedules, cluster.id]);

  const hasSchedulesAssigned = !!assignedSchedulesToCluster?.length;
  // Show view mode initially if there are schedules assigned to the cluster
  const [isEditMode, setEditMode] = useState(!hasSchedulesAssigned);

  const nodeJobConfig = useMemo(() => {
    if (!hasSchedulesAssigned) {
      return getNodeJobConfigDefaultValue(cluster.providerType);
    }

    return assignedSchedulesToCluster[0].resumeConfig.jobConfig;
  }, [hasSchedulesAssigned, assignedSchedulesToCluster, cluster.providerType]);

  const initialFormValues = useMemo(() => {
    return {
      nodeJobConfig: JSON.stringify(nodeJobConfig),
    };
  }, [nodeJobConfig]);

  useEffect(() => {
    if (hasSchedulesAssigned) {
      setEditMode(false);
    }
  }, [hasSchedulesAssigned]);

  const handleResume = (data: FormValues) => {
    createHibernationJob(
      {
        clusterId: cluster.id,
        hibernationJob: {
          type: HibernationJobType.RESUME,
          nodeConfig: JSON.parse(data.nodeJobConfig).nodeConfig,
        },
      },
      {
        onSuccess: () => {
          notify.info(
            <Stack
              direction="row"
              gap={4}
              data-testid="resumption-success-message"
            >
              <Typography variant="P14R" color="#fff">
                Resuming cluster {cluster.name}
              </Typography>
            </Stack>
          );
          onClose();
        },
        onError: () => {
          notify.error(
            <Stack direction="row" gap={4}>
              <Typography variant="P14B" color="red.200">
                Resumption failed
              </Typography>
              <Typography variant="P14R" color="#fff">
                for {cluster.name}
              </Typography>
            </Stack>
          );
        },
      }
    );
  };

  return (
    <Dialog open maxWidth="sm" testId="resume-cluster-dialog" onClose={onClose}>
      <RffForm
        initialValues={initialFormValues}
        mode={isEditMode ? 'edit' : 'view'}
        validate={getValidationSchema()}
        onSubmit={handleResume}
      >
        {({ values: formValues }) => {
          return (
            <>
              <DialogTitle sx={{ pt: 20 }}>
                <Stack direction="row" alignItems="center" gap={6}>
                  <K8sProviderIcon provider={cluster.providerType} size={24} />
                  <Typography variant="A1">Resume cluster</Typography>
                  <Divider orientation="vertical" sx={{ height: '16px' }} />
                  <Typography variant="P14R">{cluster.name}</Typography>
                </Stack>
              </DialogTitle>

              <CloseDialogButton onClose={onClose} />

              <DialogContent sx={{ pt: 0 }}>
                {isLoadingSchedules ? (
                  <Skeleton height={98} />
                ) : (
                  <DialogDescription
                    hasSchedulesAssigned={hasSchedulesAssigned}
                    assignedScheduleName={assignedSchedulesToCluster[0]?.name}
                  />
                )}
              </DialogContent>
              <DialogContent sx={{ py: 12, backgroundColor: 'grey.100' }}>
                {isLoadingSchedules ? (
                  <Skeleton height={322} />
                ) : (
                  <NodeConfiguration
                    isEditMode={isEditMode}
                    hibernationSchedules={hibernationSchedules}
                    onScheduleSelect={() => {}}
                  />
                )}
              </DialogContent>

              <Divider />

              <DialogActions
                sx={{
                  justifyContent: !isEditMode ? 'space-between' : 'flex-end',
                }}
              >
                {!isEditMode && (
                  <Button
                    testId="modify-configuration-button"
                    variant="text"
                    size="lg"
                    onClick={() => {
                      setEditMode(true);
                    }}
                  >
                    Modify configuration
                  </Button>
                )}
                <ConditionalWrapper
                  condition={!isEditMode}
                  wrapper={(child) => (
                    <Stack direction="row" gap={16}>
                      {child}
                    </Stack>
                  )}
                >
                  <Button variant="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    testId="resume-cluster-button"
                    variant="primary"
                    disabled={!formValues.nodeJobConfig}
                    loading={isResumePending}
                    type="submit"
                  >
                    Resume now
                  </Button>
                </ConditionalWrapper>
              </DialogActions>
            </>
          );
        }}
      </RffForm>
    </Dialog>
  );
};
